import React, { useState, useEffect } from 'react';
import HomeScreen from './screens/HomeScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import EmailVerificationScreen from './screens/EmailVerificationScreen';
import MenuScreen from './screens/MenuScreen';
import ProfileSettingScreen from './screens/ProfileSettingScreen';
import DetailSettingsScreen from './screens/DetailSettingsScreen';
import SettingsScreen from './screens/SettingsScreen';
import ContactScreen from './screens/ContactScreen';
import HelpScreen from './screens/HelpScreen';
import LegalScreen from './screens/LegalScreen';
import Plaza3D from './components/Plaza3D';
import { User } from './types';
import { authAPI } from './utils/api';
import './App.css';

type AppState =
    | 'home'
    | 'login'
    | 'register'
    | 'verify-email'
    | 'menu'
    | 'plaza'
    | 'profile-setting'
    | 'detail-settings'
    | 'settings'
    | 'help'
    | 'contact'
    | 'privacy-security'
    | 'security-policy'
    | 'community-guidelines'
    | 'terms-of-service'
    | 'about';

const App: React.FC = () => {
    const [currentState, setCurrentState] = useState<AppState>('home');
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [onlineUsers, setOnlineUsers] = useState<User[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
    const [isLoading, setIsLoading] = useState(true);
    const [verificationToken, setVerificationToken] = useState<string | null>(null);

    useEffect(() => {
        // URLパラメータをチェック（メール認証）
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (token) {
            setVerificationToken(token);
            setCurrentState('verify-email');
            setIsLoading(false);
            return;
        }

        // ローカルストレージからユーザー情報を取得
        const authToken = localStorage.getItem('token');
        const userData = localStorage.getItem('user');

        if (authToken && userData) {
            try {
                const user = JSON.parse(userData);
                // ユーザーのpositionが未定義の場合はデフォルト値を設定
                const userWithPosition = {
                    ...user,
                    position: user.position || { x: 0, y: 0, z: 0 },
                };
                setCurrentUser(userWithPosition);
                setCurrentState('menu');
            } catch (error) {
                console.error('ユーザーデータの解析に失敗しました:', error);
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            }
        } else {
            setCurrentState('home');
        }
        setIsLoading(false);
    }, []);

    const handleLogin = (user: User, token: string) => {
        // ユーザーのpositionが未定義の場合はデフォルト値を設定
        const userWithPosition = {
            ...user,
            position: user.position || { x: 0, y: 0, z: 0 },
        };
        setCurrentUser(userWithPosition);
        setCurrentState('menu');
    };

    const handleRegister = (user: User, token: string) => {
        // ユーザーのpositionが未定義の場合はデフォルト値を設定
        const userWithPosition = {
            ...user,
            position: user.position || { x: 0, y: 0, z: 0 },
        };
        setCurrentUser(userWithPosition);
        setCurrentState('menu');
    };

    const handleLogout = async () => {
        try {
            // サーバーにログアウトを通知
            await authAPI.logout();
        } catch (error) {
            console.error('ログアウトエラー:', error);
        } finally {
            // ローカルストレージをクリア
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setCurrentUser(null);
            setCurrentState('home');
        }
    };

    const handleUsersUpdate = (users: User[]) => {
        setOnlineUsers(users);
    };

    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    backgroundImage:
                        'url(/backgrounds/login.jpg), linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    color: 'white',
                    fontSize: '18px',
                }}
            >
                読み込み中...
            </div>
        );
    }

    if (currentState === 'home') {
        return (
            <HomeScreen
                onGoToLogin={() => setCurrentState('login')}
                onGoToRegister={() => setCurrentState('register')}
                onGoToHelp={() => {
                    // ホーム画面からヘルプへの遷移はログインが必要
                    setCurrentState('login');
                }}
                onGoToPrivacySecurity={() => {
                    // ホーム画面から規約への遷移はログインが必要
                    setCurrentState('login');
                }}
                onGoToSecurityPolicy={() => {
                    setCurrentState('login');
                }}
                onGoToCommunityGuidelines={() => {
                    setCurrentState('login');
                }}
                onGoToTermsOfService={() => {
                    setCurrentState('login');
                }}
                onGoToAbout={() => {
                    setCurrentState('login');
                }}
            />
        );
    }

    if (currentState === 'login') {
        return (
            <LoginScreen
                onLogin={handleLogin}
                onSwitchToRegister={() => setCurrentState('register')}
            />
        );
    }

    if (currentState === 'register') {
        return (
            <RegisterScreen
                onRegister={handleRegister}
                onSwitchToLogin={() => setCurrentState('login')}
            />
        );
    }

    if (currentState === 'verify-email' && verificationToken) {
        return (
            <EmailVerificationScreen
                token={verificationToken}
                onVerificationSuccess={(user) => {
                    // 認証完了後、ログイン状態にする
                    const userWithPosition = {
                        ...user,
                        position: user.position || { x: 0, y: 0, z: 0 },
                    };
                    setCurrentUser(userWithPosition);
                    setCurrentState('menu');
                    // URL をクリーン
                    window.history.replaceState({}, document.title, '/');
                }}
            />
        );
    }

    if (currentState === 'menu' && currentUser) {
        return (
            <MenuScreen
                currentUser={currentUser}
                onLogout={handleLogout}
                onGoToPlaza={() => setCurrentState('plaza')}
                onGoToProfileSetting={() => setCurrentState('profile-setting')}
                onGoToDetailSettings={() => setCurrentState('detail-settings')}
                onGoToSettings={() => setCurrentState('settings')}
                onGoToHelp={() => setCurrentState('help')}
                onGoToPrivacySecurity={() => setCurrentState('privacy-security')}
                onGoToSecurityPolicy={() => setCurrentState('security-policy')}
                onGoToCommunityGuidelines={() => setCurrentState('community-guidelines')}
                onGoToTermsOfService={() => setCurrentState('terms-of-service')}
                onGoToAbout={() => setCurrentState('about')}
                onUpdateUser={(updatedUser) => {
                    setCurrentUser(updatedUser);
                }}
            />
        );
    }

    if (currentState === 'profile-setting' && currentUser) {
        return (
            <ProfileSettingScreen
                currentUser={currentUser}
                onBack={() => setCurrentState('menu')}
                onUpdate={(updatedUser) => {
                    // ユーザー情報のみ更新、画面遷移はしない
                    setCurrentUser(updatedUser);
                }}
                onNavigateToDetailSettings={() => setCurrentState('detail-settings')}
            />
        );
    }

    if (currentState === 'detail-settings' && currentUser) {
        return (
            <DetailSettingsScreen
                currentUser={currentUser}
                onBack={() => setCurrentState('profile-setting')}
            />
        );
    }

    if (currentState === 'settings' && currentUser) {
        return (
            <SettingsScreen
                currentUser={currentUser}
                onBack={() => setCurrentState('menu')}
                onLogout={handleLogout}
            />
        );
    }

    if (currentState === 'help' && currentUser) {
        return (
            <HelpScreen
                currentUser={currentUser}
                onBack={() => setCurrentState('menu')}
                onGoToContact={() => setCurrentState('contact')}
                onGoToMenu={() => setCurrentState('menu')}
                onGoToPrivacySecurity={() => setCurrentState('privacy-security')}
                onGoToSecurityPolicy={() => setCurrentState('security-policy')}
                onGoToCommunityGuidelines={() => setCurrentState('community-guidelines')}
                onGoToTermsOfService={() => setCurrentState('terms-of-service')}
                onGoToAbout={() => setCurrentState('about')}
            />
        );
    }

    if (currentState === 'contact' && currentUser) {
        return (
            <ContactScreen 
                currentUser={currentUser} 
                onBack={() => setCurrentState('help')}
                onGoToMenu={() => setCurrentState('menu')}
                onGoToHelp={() => setCurrentState('help')}
                onGoToPrivacySecurity={() => setCurrentState('privacy-security')}
                onGoToSecurityPolicy={() => setCurrentState('security-policy')}
                onGoToCommunityGuidelines={() => setCurrentState('community-guidelines')}
                onGoToTermsOfService={() => setCurrentState('terms-of-service')}
                onGoToAbout={() => setCurrentState('about')}
            />
        );
    }

    if (currentState === 'plaza' && currentUser) {
        return (
            <Plaza3D
                currentUser={currentUser}
                onUsersUpdate={handleUsersUpdate}
                onBackToRoom={() => setCurrentState('menu')}
            />
        );
    }

    if (currentState === 'privacy-security' && currentUser) {
        return (
            <LegalScreen
                currentUser={currentUser}
                title='プライバシーセキュリティ'
                content={
                    <div>
                        <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>
                            プライバシーセキュリティ
                        </h2>
                        <p style={{ marginBottom: '15px' }}>
                            Voitter（以下「当サービス」）は、ユーザーの個人情報の保護を最優先に考えております。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            個人情報の取り扱い
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスでは、サービスの提供に必要な範囲でのみ個人情報を収集いたします。収集する情報には、ユーザー名、メールアドレス、プロフィール画像等が含まれます。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            セキュリティ対策
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスは、SSL/TLS暗号化通信を使用し、データベースへのアクセス制御を実施しております。また、定期的なセキュリティ監査を実施し、不正アクセスや情報漏洩の防止に努めております。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            情報の第三者提供
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスは、法律で定められた場合を除き、ユーザーの同意なく個人情報を第三者に提供することはございません。
                        </p>
                        <p
                            style={{
                                marginTop: '30px',
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            最終更新日: 2025年1月
                        </p>
                    </div>
                }
                onBack={() => setCurrentState('menu')}
                currentPage="privacy-security"
                onGoToMenu={() => setCurrentState('menu')}
                onGoToHelp={() => setCurrentState('help')}
                onGoToContact={() => setCurrentState('contact')}
                onGoToPrivacySecurity={() => setCurrentState('privacy-security')}
                onGoToSecurityPolicy={() => setCurrentState('security-policy')}
                onGoToCommunityGuidelines={() => setCurrentState('community-guidelines')}
                onGoToTermsOfService={() => setCurrentState('terms-of-service')}
                onGoToAbout={() => setCurrentState('about')}
            />
        );
    }

    if (currentState === 'security-policy' && currentUser) {
        return (
            <LegalScreen
                currentUser={currentUser}
                title='情報セキュリティ基本方針'
                content={
                    <div>
                        <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>
                            情報セキュリティ基本方針
                        </h2>
                        <p style={{ marginBottom: '15px' }}>
                            Voitter（以下「当サービス」）は、ユーザーの情報資産を適切に保護するため、以下の基本方針に基づいて情報セキュリティ対策を実施いたします。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            1. 基本方針
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスは、情報セキュリティに関する法令、規制、および社会的責任を認識し、情報セキュリティマネジメントシステムを適切に運用いたします。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            2. 情報資産の保護
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスは、ユーザーの個人情報、通話記録、その他の機密情報を適切に管理し、不正アクセス、漏洩、改ざん、破壊などのリスクから保護いたします。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            3. セキュリティ対策
                        </h3>
                        <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
                            <li style={{ marginBottom: '10px' }}>通信の暗号化（SSL/TLS）</li>
                            <li style={{ marginBottom: '10px' }}>アクセス制御と認証</li>
                            <li style={{ marginBottom: '10px' }}>定期的なセキュリティ監査</li>
                            <li style={{ marginBottom: '10px' }}>インシデント対応体制の整備</li>
                        </ul>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            4. 継続的改善
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスは、情報セキュリティマネジメントシステムを継続的に見直し、改善してまいります。
                        </p>
                        <p
                            style={{
                                marginTop: '30px',
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            最終更新日: 2025年1月
                        </p>
                    </div>
                }
                onBack={() => setCurrentState('menu')}
                currentPage="security-policy"
                onGoToMenu={() => setCurrentState('menu')}
                onGoToHelp={() => setCurrentState('help')}
                onGoToContact={() => setCurrentState('contact')}
                onGoToPrivacySecurity={() => setCurrentState('privacy-security')}
                onGoToSecurityPolicy={() => setCurrentState('security-policy')}
                onGoToCommunityGuidelines={() => setCurrentState('community-guidelines')}
                onGoToTermsOfService={() => setCurrentState('terms-of-service')}
                onGoToAbout={() => setCurrentState('about')}
            />
        );
    }

    if (currentState === 'community-guidelines' && currentUser) {
        return (
            <LegalScreen
                currentUser={currentUser}
                title='コミュニティガイドライン'
                content={
                    <div>
                        <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>
                            コミュニティガイドライン
                        </h2>
                        <p style={{ marginBottom: '15px' }}>
                            Voitterは、すべてのユーザーが安心して利用できるコミュニティを目指しています。以下のガイドラインを守って、楽しいコミュニケーションをお楽しみください。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            1. 基本的なマナー
                        </h3>
                        <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
                            <li style={{ marginBottom: '10px' }}>
                                相手を尊重し、礼儀正しく接してください
                            </li>
                            <li style={{ marginBottom: '10px' }}>差別的、攻撃的な発言は禁止です</li>
                            <li style={{ marginBottom: '10px' }}>プライバシーを尊重してください</li>
                            <li style={{ marginBottom: '10px' }}>
                                スパム行為や不適切な広告は禁止です
                            </li>
                        </ul>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            2. 禁止行為
                        </h3>
                        <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
                            <li style={{ marginBottom: '10px' }}>ハラスメントや嫌がらせ行為</li>
                            <li style={{ marginBottom: '10px' }}>違法行為の助長や推奨</li>
                            <li style={{ marginBottom: '10px' }}>
                                なりすましやアカウントの不正利用
                            </li>
                            <li style={{ marginBottom: '10px' }}>個人情報の無断公開</li>
                            <li style={{ marginBottom: '10px' }}>システムの不正利用や攻撃</li>
                        </ul>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            3. 通話時の注意事項
                        </h3>
                        <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
                            <li style={{ marginBottom: '10px' }}>
                                相手の同意なく通話内容を録音・録画しないでください
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                不適切な音声コンテンツの配信は禁止です
                            </li>
                            <li style={{ marginBottom: '10px' }}>
                                通話中の迷惑行為は慎んでください
                            </li>
                        </ul>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            4. 違反時の措置
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            ガイドラインに違反した場合は、警告、アカウントの一時停止、または永久停止の措置を講じることがあります。
                        </p>
                        <p
                            style={{
                                marginTop: '30px',
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            最終更新日: 2025年1月
                        </p>
                    </div>
                }
                onBack={() => setCurrentState('menu')}
                currentPage="community-guidelines"
                onGoToMenu={() => setCurrentState('menu')}
                onGoToHelp={() => setCurrentState('help')}
                onGoToContact={() => setCurrentState('contact')}
                onGoToPrivacySecurity={() => setCurrentState('privacy-security')}
                onGoToSecurityPolicy={() => setCurrentState('security-policy')}
                onGoToCommunityGuidelines={() => setCurrentState('community-guidelines')}
                onGoToTermsOfService={() => setCurrentState('terms-of-service')}
                onGoToAbout={() => setCurrentState('about')}
            />
        );
    }

    if (currentState === 'terms-of-service' && currentUser) {
        return (
            <LegalScreen
                currentUser={currentUser}
                title='利用規約'
                content={
                    <div>
                        <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>利用規約</h2>
                        <p style={{ marginBottom: '15px' }}>
                            本規約は、Voitter（以下「当サービス」）の利用条件を定めるものです。当サービスを利用するすべてのユーザーは、本規約に同意したものとみなされます。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            第1条（定義）
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            本規約において、「サービス」とは、Voitterが提供する音声通話マッチングサービスを指します。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            第2条（利用登録）
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            ユーザーは、当サービスを利用するために、正確な情報を提供し、利用登録を行うものとします。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            第3条（利用料金）
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスの基本機能は無料でご利用いただけます。一部の機能については有料となります。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            第4条（禁止行為）
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            ユーザーは、法令、公序良俗に反する行為、他のユーザーに迷惑をかける行為、当サービスの運営を妨げる行為を行ってはなりません。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            第5条（免責事項）
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスは、ユーザー間の通話内容について一切の責任を負いません。また、サービスの中断、終了、またはデータの消失についても責任を負いません。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            第6条（規約の変更）
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            当サービスは、ユーザーに通知することなく本規約を変更することがあります。
                        </p>
                        <p
                            style={{
                                marginTop: '30px',
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            最終更新日: 2025年1月
                        </p>
                    </div>
                }
                onBack={() => setCurrentState('menu')}
                currentPage="terms-of-service"
                onGoToMenu={() => setCurrentState('menu')}
                onGoToHelp={() => setCurrentState('help')}
                onGoToContact={() => setCurrentState('contact')}
                onGoToPrivacySecurity={() => setCurrentState('privacy-security')}
                onGoToSecurityPolicy={() => setCurrentState('security-policy')}
                onGoToCommunityGuidelines={() => setCurrentState('community-guidelines')}
                onGoToTermsOfService={() => setCurrentState('terms-of-service')}
                onGoToAbout={() => setCurrentState('about')}
            />
        );
    }

    if (currentState === 'about' && currentUser) {
        return (
            <LegalScreen
                currentUser={currentUser}
                title='運営者について'
                content={
                    <div>
                        <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>運営者について</h2>
                        <p style={{ marginBottom: '15px' }}>
                            Voitterは、個人事業主により運営されている音声通話マッチングサービスです。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            運営者情報
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            本サービスは個人事業主により運営されております。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            サービスについて
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            Voitterは、ユーザー同士が音声通話を通じてコミュニケーションを取るためのマッチングサービスです。気軽に会話を楽しみたい方、新しい出会いを求める方、練習や勉強のための会話パートナーを探している方など、様々な用途でご利用いただけます。
                        </p>
                        <h3 style={{ fontSize: '18px', marginTop: '25px', marginBottom: '15px' }}>
                            お問い合わせ
                        </h3>
                        <p style={{ marginBottom: '15px' }}>
                            ご質問やお問い合わせがございましたら、メニュー画面の「ご利用案内」内の「ヘルプ」から「お問い合わせ」フォームをご利用ください。
                        </p>
                        <p
                            style={{
                                marginTop: '30px',
                                fontSize: '12px',
                                color: '#666',
                            }}
                        >
                            最終更新日: 2025年1月
                        </p>
                    </div>
                }
                onBack={() => setCurrentState('menu')}
                currentPage="about"
                onGoToMenu={() => setCurrentState('menu')}
                onGoToHelp={() => setCurrentState('help')}
                onGoToContact={() => setCurrentState('contact')}
                onGoToPrivacySecurity={() => setCurrentState('privacy-security')}
                onGoToSecurityPolicy={() => setCurrentState('security-policy')}
                onGoToCommunityGuidelines={() => setCurrentState('community-guidelines')}
                onGoToTermsOfService={() => setCurrentState('terms-of-service')}
                onGoToAbout={() => setCurrentState('about')}
            />
        );
    }

    return null;
};

export default App;
